/** The board's headline: paid seats, assigned, unused, expiring, and waste.
 *
 *  Every figure here excludes free/unlimited SKUs, tenant-capacity SKUs, and
 *  suspended subscriptions — the backend does that filtering and reports how
 *  many SKUs it dropped, which the caption states outright. A headline nobody
 *  can reconcile is a headline nobody acts on.
 *
 *  **Paid seats is `enabled` alone.** Expiring capacity gets its own tile
 *  rather than being folded into the total: a subscription inside its grace
 *  period has ended, and adding those seats to what the tenant pays for counts
 *  a renewal twice — on this tenant that inflated unused seats by 3,693.
 *
 *  Suspended capacity gets a tile for the same reason. `prepaidUnits` has four
 *  buckets, and the two cancelled ones were being ingested and never shown, so
 *  a cancelled subscription with seats still on it was invisible here. It is
 *  its own number, added to nothing — including to the locked-out figure it
 *  reports in its caption. */

import { Card, Statistic } from "@norican/react";

import type { LicenseTotals } from "@/lib/api";

import {
  cancelledSeatsCaption,
  formatMoney,
  formatPct,
  formatSeats,
  SEAT_LABEL,
  utilisationTone,
  wasteTone,
} from "./licence-format";

interface LicenceKpiRowProps {
  totals: LicenseTotals;
}

export function LicenceKpiRow({ totals }: LicenceKpiRowProps) {
  const waste = formatMoney(totals.estimated_monthly_waste, totals.currency);
  const usageTone = utilisationTone(totals.utilisation_pct);
  const excluded =
    totals.excluded_skus > 0
      ? `${totals.counted_skus} paid SKUs · ${totals.excluded_skus} excluded`
      : `${totals.counted_skus} paid SKUs`;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      <Card variant="outline" accent="left" accentTone="accent">
        <Card.Body>
          <Statistic
            label={SEAT_LABEL.purchased}
            value={formatSeats(totals.purchased_seats)}
            size="lg"
            caption={excluded}
          />
        </Card.Body>
      </Card>

      <Card
        variant="outline"
        accent="left"
        accentTone={usageTone === "danger" ? "danger" : "accent"}
      >
        <Card.Body>
          <Statistic
            label={SEAT_LABEL.assigned}
            value={formatSeats(totals.assigned_seats)}
            size="lg"
            progress={Math.min(totals.utilisation_pct ?? 0, 100)}
            progressTone={usageTone === "neutral" ? "accent" : usageTone}
            caption={`${formatPct(totals.utilisation_pct)} of paid seats in use`}
          />
        </Card.Body>
      </Card>

      <Card
        variant="outline"
        accent="left"
        accentTone={totals.unused_seats > 0 ? "warning" : "success"}
      >
        <Card.Body>
          <Statistic
            label="Unused seats"
            value={formatSeats(totals.unused_seats)}
            tone={wasteTone(totals.unused_seats)}
            size="lg"
            caption={
              totals.unused_seats > 0 ? "Paid for, assigned to nobody" : "Nothing sitting idle"
            }
          />
        </Card.Body>
      </Card>

      {/* Expiring capacity stands on its own, never inside Paid seats. The
          caption names the consequence rather than the accounting: what
          matters about grace is who loses access when it ends. */}
      <Card
        variant="outline"
        accent="left"
        accentTone={totals.over_assigned_seats > 0 ? "danger" : "neutral"}
      >
        <Card.Body>
          <Statistic
            label={SEAT_LABEL.grace}
            value={formatSeats(totals.grace_period_seats)}
            tone={totals.grace_period_seats > 0 ? "warning" : "neutral"}
            size="lg"
            caption={
              totals.over_assigned_seats > 0
                ? `${formatSeats(totals.over_assigned_seats)} assignments end with it`
                : "Expired seats, not counted as paid"
            }
          />
        </Card.Body>
      </Card>

      {/* Cancelled capacity: ingested from `prepaidUnits` all along, shown
          nowhere until now. Seats sitting on a cancelled subscription are not
          assignable, so like grace they are reported beside the paid total and
          never inside it. */}
      <Card
        variant="outline"
        accent="left"
        accentTone={
          totals.suspended_seats > 0 || totals.locked_out_seats > 0 ? "warning" : "neutral"
        }
      >
        <Card.Body>
          <Statistic
            label={SEAT_LABEL.suspended}
            value={formatSeats(totals.suspended_seats)}
            tone={totals.suspended_seats > 0 ? "warning" : "neutral"}
            size="lg"
            caption={cancelledSeatsCaption(totals.suspended_seats, totals.locked_out_seats)}
          />
        </Card.Body>
      </Card>

      <Card variant="outline" accent="left" accentTone={waste ? "danger" : "neutral"}>
        <Card.Body>
          {waste ? (
            <Statistic
              label="Estimated monthly waste"
              value={waste}
              tone="danger"
              size="lg"
              caption="Estimated, from configured rates"
            />
          ) : (
            /* Microsoft Graph carries no price data at all. Rather than invent
               a plausible number, say so and name the fix. */
            <Statistic
              label="Estimated monthly waste"
              value="Not costed"
              tone="neutral"
              size="lg"
              caption="Set per-SKU rates to cost the unused seats"
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
